import { ApplicationConfig, importProvidersFrom, provideZoneChangeDetection, ErrorHandler } from '@angular/core';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideRouter } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';

import { getFunctions, provideFunctions, connectFunctionsEmulator } from '@angular/fire/functions';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { getStorage, provideStorage } from '@angular/fire/storage';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { getAnalytics, provideAnalytics, ScreenTrackingService, UserTrackingService } from '@angular/fire/analytics';

import { TranslateModule, TranslateLoader } from '@ngx-translate/core';

import { provideToastr } from 'ngx-toastr';
import { BsDropdownConfig } from 'ngx-bootstrap/dropdown';

import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';

import { provideCharts, withDefaultRegisterables } from 'ng2-charts';

import { routes } from './app.routes';
import { rootReducer } from './store';

// Import the necessary effects
import { AuthenticationEffects } from './store/Authentication/authentication.effects';
import { ChatEffects } from './store/Chat/chat.effect';

// Import global error handler
import { GlobalErrorHandler } from './core/helpers/global-error.handler';

import { TRANSLATE_HTTP_LOADER_CONFIG, TranslateHttpLoader } from '@ngx-translate/http-loader';
import { LanguageService } from './core/services/language.service';
import { provideFlatpickrDefaults } from 'angularx-flatpickr';
import { NgxEditorModule } from 'ngx-editor';
import { environment } from '../environments/environment';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    provideAuth(() => getAuth()),
    provideAnalytics(() => getAnalytics()),
    ScreenTrackingService,
    UserTrackingService,
    provideFunctions(() => getFunctions(undefined, 'europe-west1')),
    provideFirestore(() => getFirestore()),
    provideStorage(() => getStorage()),
    {
      provide: TRANSLATE_HTTP_LOADER_CONFIG,
      useValue: {
        prefix: 'assets/i18n/',
        suffix: '.json'
      },
    },
    provideFlatpickrDefaults(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    provideToastr(),
    provideCharts(withDefaultRegisterables()),
    provideStore(rootReducer),
    importProvidersFrom(
      NgxEditorModule.forRoot({
        locals: {
          // menu
          bold: 'Bold',
          italic: 'Italic',
          code: 'Code',
          blockquote: 'Blockquote',
          underline: 'Underline',
          strike: 'Strike',
          bullet_list: 'Bullet List',
          ordered_list: 'Ordered List',
          heading: 'Heading',
          h1: 'Header 3',
          h2: 'Header 2',
          h3: 'Header 3',
          h4: 'Header 4',
          h5: 'Header 5',
          h6: 'Header 6',
          align_left: 'Left Align',
          align_center: 'Center Align',
          align_right: 'Right Align',
          align_justify: 'Justify',
          text_color: 'Text Color',
          background_color: 'Background Color',

          // popups, forms, others...
          url: 'URL',
          text: 'Text',
          openInNewTab: 'Open in new tab',
          insert: 'Insert',
          altText: 'Alt Text',
          title: 'Title',
          remove: 'Remove',
          enterValidUrl: 'Please enter a valid URL',
          required: 'This is required'
        },
      }),),
    provideEffects([

      AuthenticationEffects,

      ChatEffects,


      LanguageService
    ]),

    // 🔑 CORRECTLY CONFIGURE TranslateModule using the imported function
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
        fallbackLang: 'en', // Set your fallback language
      })

    ),


    { provide: BsDropdownConfig, useValue: { isAnimated: true, autoClose: true } },

    // Global error handler with analytics integration
    { provide: ErrorHandler, useClass: GlobalErrorHandler }
  ]
};